import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../home/components/Footer';
import { mockCreators, getSimilarCreators } from '../../../mocks/creators';
import { aiCreators, AICreator } from '../../../mocks/aiCreators';
import { createCreatorUrl, createAIProjectUrl, generateBreadcrumbs } from '../../../hooks/useNavigation';
import MentionedInArticles from './components/MentionedInArticles';
import { useAuth } from '../../../contexts/AuthContext';
import { AuthModal } from '../../../components/feature/AuthModal';
import ShareModal from '../../../components/base/ShareModal';
import { useCreatorStats } from '../../../hooks/useCreatorStats';
import { supabase } from '../../../lib/supabase';

// Helper to convert AI creator to a compatible shape
const mapAICreatorToProfile = (ai: AICreator) => ({
  id: ai.id,
  slug: ai.slug,
  name: ai.name,
  role: ai.role,
  country: ai.country,
  avatar: ai.avatar,
  coverImage: ai.coverImage,
  followers: 0,
  following: 0,
  verified: ai.verified,
  recentWork: ai.featuredWork,
  bio: ai.bio,
  projects: ai.projects,
  awards: 0,
  yearsActive: 0,
  specialties: [ai.category],
  languages: [],
  location: ai.country,
  fanRating: 0,
  totalReviews: 0,
  responseRate: 0,
  avgResponseTime: 'N/A',
  socialMedia: { instagram: '', twitter: '', website: '' },
  // AI-specific fields
  isAICreator: true as const,
  tools: ai.tools,
  featuredWork: ai.featuredWork,
  category: ai.category,
  followersDisplay: ai.followers,
});

const CreatorDetailPage = () => {
  const { urlParam } = useParams<{ urlParam: string }>();
  
  // Find creator by slug — check regular creators first, then AI creators
  const { creator, aiCreatorData } = useMemo(() => {
    const regular = mockCreators.find(c => c.slug === urlParam);
    if (regular) return { creator: regular, aiCreatorData: null };

    const ai = aiCreators.find(c => c.slug === urlParam);
    if (ai) return { creator: mapAICreatorToProfile(ai), aiCreatorData: ai };

    return { creator: null, aiCreatorData: null };
  }, [urlParam]);

  const isAICreator = !!aiCreatorData;

  // Get similar creators
  const similarCreators = useMemo(() => {
    if (!creator) return [];
    if (isAICreator) {
      // Show other AI creators in the same category
      return aiCreators
        .filter(c => c.id !== creator.id)
        .sort((a, b) => {
          if (a.category === aiCreatorData!.category && b.category !== aiCreatorData!.category) return -1;
          if (b.category === aiCreatorData!.category && a.category !== aiCreatorData!.category) return 1;
          return 0;
        })
        .slice(0, 3)
        .map(c => ({
          id: c.id,
          slug: c.slug,
          name: c.name,
          role: c.role,
          avatar: c.avatar,
          followers: 0,
          followersDisplay: c.followers,
        }));
    }
    return getSimilarCreators(creator.id, 3).map(c => ({
      ...c,
      followersDisplay: undefined,
    }));
  }, [creator, isAICreator, aiCreatorData]);

  // Generate breadcrumbs
  const breadcrumbs = useMemo(() => {
    if (!creator) return [];
    return generateBreadcrumbs('creator', { name: creator.name });
  }, [creator]);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [fanMessage, setFanMessage] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [dbCreatorId, setDbCreatorId] = useState<string | null>(null);

  const { isAuthenticated, user } = useAuth();

  // Lookup the creator's database UUID by slug
  useEffect(() => {
    if (!creator) return;
    let mounted = true;
    const lookupCreator = async () => {
      const { data } = await supabase
        .from('creators')
        .select('id')
        .eq('slug', creator.slug)
        .maybeSingle();
      if (mounted && data) {
        setDbCreatorId(data.id);
      }
    };
    lookupCreator();
    return () => { mounted = false; };
  }, [creator]);

  // Real-time stats hook
  const {
    followerCount,
    shareCount,
    isFollowing,
    loading: statsLoading,
    toggleFollow,
    incrementShareCount,
  } = useCreatorStats(dbCreatorId, user?.id || null);

  // Hero carousel state
  const heroCarouselRef = useRef<HTMLDivElement>(null);
  const [isHeroHovered, setIsHeroHovered] = useState(false);

  // Hero filmography showcase data (for regular creators)
  const heroShowcase = isAICreator ? [] : [
    {
      id: 0,
      title: 'Welcome to Filmora',
      subtitle: 'Discover the Art of Storytelling',
      description: 'Experience breathtaking performances and captivating narratives from the finest talents in cinema',
      image: 'https://public.readdy.ai/ai/img_res/4313d6b9ec427bc842209ea9bafba187.jpg',
      type: 'welcome'
    },
    {
      id: 1,
      title: 'Noor',
      subtitle: 'A Tale of Light and Shadow',
      description: 'Award-winning drama exploring the depths of human emotion',
      image: 'https://readdy.ai/api/search-image?query=Cinematic%20movie%20scene%20elegant%20Middle%20Eastern%20woman%20in%20flowing%20silk%20dress%20standing%20in%20ornate%20palace%20with%20golden%20sunlight%20streaming%20through%20arabesque%20windows%20dramatic%20film%20lighting%20warm%20amber%20tones%20professional%20cinematography&width=1400&height=500&seq=hero-noor&orientation=landscape',
      year: 2024,
      rating: 4.8
    },
    {
      id: 2,
      title: 'Desert Rose',
      subtitle: 'Where Dreams Meet Destiny',
      description: 'An epic journey through time and tradition',
      image: 'https://readdy.ai/api/search-image?query=Breathtaking%20desert%20landscape%20at%20golden%20hour%20with%20silhouette%20of%20elegant%20woman%20in%20traditional%20dress%20standing%20on%20sand%20dune%20dramatic%20sky%20with%20warm%20orange%20and%20pink%20colors%20cinematic%20wide%20shot%20professional%20film%20photography&width=1400&height=500&seq=hero-desert&orientation=landscape',
      year: 2023,
      rating: 4.6
    },
    {
      id: 3,
      title: 'City Lights',
      subtitle: 'Modern Love Stories',
      description: 'Contemporary drama set against a stunning urban backdrop',
      image: 'https://readdy.ai/api/search-image?query=Modern%20cityscape%20at%20night%20with%20beautiful%20woman%20looking%20out%20floor%20to%20ceiling%20windows%20at%20illuminated%20skyline%20bokeh%20city%20lights%20reflection%20dramatic%20cinematic%20lighting%20contemporary%20film%20scene%20professional%20photography&width=1400&height=500&seq=hero-city&orientation=landscape',
      year: 2024,
      rating: 4.9
    },
    {
      id: 4,
      title: 'Whispers of Time',
      subtitle: 'Echoes of the Past',
      description: 'A haunting period piece that transcends generations',
      image: 'https://readdy.ai/api/search-image?query=Vintage%20historical%20drama%20scene%20elegant%20woman%20in%20period%20costume%20standing%20in%20grand%20library%20with%20antique%20books%20dramatic%20candlelight%20warm%20sepia%20tones%20cinematic%20composition%20professional%20film%20photography%20atmospheric&width=1400&height=500&seq=hero-whispers&orientation=landscape',
      year: 2022,
      rating: 4.7
    },
    {
      id: 5,
      title: 'Eternal Bonds',
      subtitle: 'Love Beyond Boundaries',
      description: 'A powerful story of connection and sacrifice',
      image: 'https://readdy.ai/api/search-image?query=Romantic%20cinematic%20scene%20couple%20silhouette%20against%20dramatic%20sunset%20sky%20on%20cliff%20overlooking%20ocean%20waves%20crashing%20below%20golden%20hour%20lighting%20emotional%20film%20moment%20professional%20cinematography%20wide%20angle&width=1400&height=500&seq=hero-eternal&orientation=landscape',
      year: 2024,
      rating: 4.8
    }
  ];

  // Auto-scroll effect for hero carousel (only for regular creators)
  useEffect(() => {
    if (isAICreator) return;
    const carousel = heroCarouselRef.current;
    if (!carousel || isHeroHovered) return;

    const scrollSpeed = 0.5;
    let animationId: number;
    const totalWidth = carousel.scrollWidth / 2;

    const scroll = () => {
      if (carousel.scrollLeft >= totalWidth) {
        carousel.scrollLeft = 0;
      } else {
        carousel.scrollLeft += scrollSpeed;
      }
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isHeroHovered, isAICreator]);

  // Mock filmography data
  const filmography = [
    {
      id: 1,
      title: 'Noor',
      year: 2024,
      role: creator?.role === 'Director' ? 'Director' : 'Lead Role',
      poster: 'https://readdy.ai/api/search-image?query=Movie%20poster%20for%20Middle%20Eastern%20drama%20film%20featuring%20elegant%20actress%20cinematic%20design%20with%20Arabic%20typography%20and%20warm%20desert%20sunset%20colors%20professional%20film%20poster&width=180&height=270&seq=film1&orientation=portrait',
      rating: 4.8,
      status: 'Released',
      episodes: 12,
      genre: 'Drama, Romance'
    },
    {
      id: 2,
      title: 'Desert Rose',
      year: 2023,
      role: creator?.role === 'Director' ? 'Director' : 'Supporting Role',
      poster: 'https://readdy.ai/api/search-image?query=Film%20poster%20for%20Arab%20drama%20featuring%20desert%20landscape%20with%20elegant%20woman%20silhouette%20at%20sunset%20artistic%20movie%20poster%20design%20with%20warm%20amber%20tones&width=180&height=270&seq=film2&orientation=portrait',
      rating: 4.6,
      status: 'Released',
      episodes: 8,
      genre: 'Historical, Drama'
    },
    {
      id: 3,
      title: 'City Lights',
      year: 2024,
      role: creator?.role === 'Director' ? 'Director' : 'Lead Role',
      poster: 'https://readdy.ai/api/search-image?query=Modern%20urban%20drama%20movie%20poster%20with%20Middle%20Eastern%20actress%20against%20illuminated%20city%20skyline%20at%20night%20contemporary%20film%20poster%20design&width=180&height=270&seq=film3&orientation=portrait',
      rating: 4.9,
      status: 'Post-Production',
      episodes: 10,
      genre: 'Contemporary, Drama'
    }
  ];

  // Mock awards data
  const awards = [
    { year: 2024, title: 'Best Performance', event: 'Dubai International Film Festival', project: 'Noor', icon: 'ri-trophy-line', color: 'text-yellow-600 bg-yellow-100' },
    { year: 2023, title: 'Rising Star Award', event: 'Arab Cinema Awards', project: 'Desert Rose', icon: 'ri-star-line', color: 'text-rose-600 bg-rose-100' },
    { year: 2022, title: 'Audience Choice', event: 'Cairo Film Festival', project: 'Whispers of Time', icon: 'ri-heart-line', color: 'text-pink-600 bg-pink-100' }
  ];

  // Mock fan reviews
  const fanReviews = [
    {
      id: 1,
      user: 'Fatima Ahmed',
      avatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20young%20Middle%20Eastern%20woman%20with%20friendly%20smile%20casual%20headshot%20natural%20lighting%20clean%20background&width=50&height=50&seq=fan1&orientation=squarish',
      rating: 5,
      date: '2 days ago',
      comment: `${creator?.name}'s performance was absolutely breathtaking! Such depth and emotion in every scene.`,
      likes: 234
    },
    {
      id: 2,
      user: 'Mohammed Hassan',
      avatar: 'https://readdy.ai/api/search-image?query=Portrait%20of%20young%20Middle%20Eastern%20man%20with%20friendly%20expression%20casual%20headshot%20natural%20lighting%20clean%20background&width=50&height=50&seq=fan2&orientation=squarish',
      rating: 5,
      date: '5 days ago',
      comment: 'One of the most talented artists in Arab cinema today. Their dedication to authentic storytelling is inspiring.',
      likes: 189
    }
  ];

  const formatFollowers = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
    return count.toString();
  };

  const handleSendMessage = () => {
    if (fanMessage.trim()) {
      alert(`Message sent! ${creator?.name} typically responds within ${creator?.avgResponseTime}.`);
      setFanMessage('');
    }
  };

  const handleFollow = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    toggleFollow();
  };

  const handleShare = () => {
    incrementShareCount();
  };

  const profileUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}`
    : '';

  if (!creator) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <i className="ri-user-line text-6xl text-gray-300 mb-4"></i>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Creator Not Found</h1>
          <p className="text-gray-600 mb-6">The creator you&apos;re looking for doesn&apos;t exist.</p>
          <Link to="/creators" className="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors">
            Browse All Creators
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Tabs differ for AI vs regular creators
  const tabs = isAICreator
    ? [
        { id: 'overview', label: 'Overview', icon: 'ri-user-line' },
        { id: 'projects', label: 'Projects', icon: 'ri-film-line' },
        { id: 'reviews', label: 'Fan Reviews', icon: 'ri-chat-3-line' },
      ]
    : [
        { id: 'overview', label: 'Overview', icon: 'ri-user-line' },
        { id: 'filmography', label: 'Filmography', icon: 'ri-film-line' },
        { id: 'awards', label: 'Awards', icon: 'ri-award-line' },
        { id: 'reviews', label: 'Fan Reviews', icon: 'ri-chat-3-line' },
      ];

  // Use real-time follower count if DB creator exists, otherwise fallback to mock
  const displayFollowerCount = dbCreatorId ? followerCount : (isAICreator ? 0 : creator.followers);
  const displayShareCount = dbCreatorId ? shareCount : 0;
  const displayIsFollowing = dbCreatorId ? isFollowing : false;

  // Accent colors for AI vs regular
  const accentFrom = isAICreator ? 'from-orange-500' : 'from-rose-600';
  const accentTo = isAICreator ? 'to-rose-500' : 'to-amber-600';
  const accentText = isAICreator ? 'text-orange-600' : 'text-rose-600';
  const accentBg = isAICreator ? 'bg-orange-50' : 'bg-rose-50';
  const accentBgHover = isAICreator ? 'hover:bg-orange-100' : 'hover:bg-rose-100';
  const accentBorder = isAICreator ? 'hover:border-orange-200' : 'hover:border-rose-200';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Back Button + Breadcrumbs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4">
          <Link
            to="/creators"
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium ${accentText} ${accentBg} ${accentBgHover} rounded-lg transition-colors whitespace-nowrap cursor-pointer`}
          >
            <i className="ri-arrow-left-line"></i>
            Back to Creators
          </Link>
          <nav className="flex items-center space-x-2 text-sm">
            {breadcrumbs.map((crumb, index) => (
              <span key={index} className="flex items-center">
                {index > 0 && <i className="ri-arrow-right-s-line text-gray-400 mx-2"></i>}
                {crumb.href === '#' ? (
                  <span className="text-gray-900 font-medium">{crumb.label}</span>
                ) : (
                  <Link
                    to={crumb.href}
                    className={`text-gray-500 ${accentText ? `hover:${accentText}` : ''} transition-colors`}
                  >
                    {crumb.label}
                  </Link>
                )}
              </span>
            ))}
          </nav>
        </div>
      </div>
      
      {/* Hero Section */}
      {isAICreator ? (
        /* AI Creator Hero — cover image with overlay */
        <div className="relative bg-black">
          <div className="relative w-full h-[360px] md:h-[440px]">
            <img
              src={aiCreatorData!.coverImage}
              alt={`${creator.name} cover`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40"></div>
          </div>

          {/* AI Badge */}
          <div className="absolute top-6 right-6 md:top-8 md:right-8 z-10">
            <span className="px-3 py-1.5 bg-gradient-to-r from-orange-500 to-rose-500 text-white text-xs font-bold rounded-full flex items-center gap-1.5 shadow-lg">
              <i className="ri-robot-2-fill"></i>
              AI Creator
            </span>
          </div>

          {/* Creator Profile Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 z-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col md:flex-row md:items-end gap-5">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <img
                      src={creator.avatar}
                      alt={creator.name}
                      className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-[3px] border-white shadow-2xl"
                    />
                    {creator.verified && (
                      <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-gradient-to-br from-orange-500 to-rose-500 rounded-full flex items-center justify-center border-2 border-white">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                    )}
                  </div>
                  <div className="text-white">
                    <h1 className="text-2xl md:text-3xl font-bold">{creator.name}</h1>
                    <p className="text-orange-300 text-sm font-medium mt-0.5">{creator.role}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-sm text-gray-300">
                      <span className="flex items-center gap-1">
                        <i className="ri-map-pin-line text-xs"></i>
                        {creator.country}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-price-tag-3-line text-xs"></i>
                        {aiCreatorData!.category}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex-1"></div>
                <div className="flex items-center space-x-3">
                  {displayShareCount > 0 && (
                    <div className="flex items-center space-x-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white/80 text-sm">
                      <i className="ri-share-forward-line text-xs"></i>
                      <span>{formatFollowers(displayShareCount)} shares</span>
                    </div>
                  )}
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer bg-white/20 text-white border border-white/40 hover:bg-white/30"
                  >
                    <i className="ri-share-line mr-2"></i>
                    Share Profile
                  </button>
                  <button
                    onClick={handleFollow}
                    className={`px-5 py-2 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                      displayIsFollowing
                        ? 'bg-white/20 text-white border border-white/40 hover:bg-white/30'
                        : 'bg-gradient-to-r from-orange-500 to-rose-500 text-white hover:from-orange-600 hover:to-rose-600'
                    }`}
                  >
                    <i className={`${displayIsFollowing ? 'ri-check-line' : 'ri-user-add-line'}`}></i>
                    {displayIsFollowing ? 'Following' : 'Follow'}
                    {dbCreatorId && (
                      <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs font-medium ml-0.5">
                        {formatFollowers(displayFollowerCount)}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Regular Creator Hero — auto-scrolling carousel */
        <div className="relative bg-black">
          <div 
            ref={heroCarouselRef}
            className="flex overflow-x-hidden"
            onMouseEnter={() => setIsHeroHovered(true)}
            onMouseLeave={() => setIsHeroHovered(false)}
            style={{ scrollBehavior: 'auto' }}
          >
            {[...heroShowcase, ...heroShowcase].map((item, index) => (
              <div 
                key={`${item.id}-${index}`}
                className="relative flex-shrink-0 w-full md:w-[700px] h-[400px] md:h-[500px]"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60"></div>
                
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                  {item.type === 'welcome' ? (
                    <div className="max-w-xl">
                      <div className="flex items-center space-x-2 mb-3">
                        <i className="ri-film-line text-amber-400 text-2xl"></i>
                        <span className="text-amber-400 font-semibold tracking-wider uppercase text-sm">Filmora Presents</span>
                      </div>
                      <h2 className="text-3xl md:text-5xl font-bold text-white mb-3 leading-tight">{item.title}</h2>
                      <p className="text-xl md:text-2xl text-amber-200 mb-3">{item.subtitle}</p>
                      <p className="text-gray-300 text-sm md:text-base max-w-md">{item.description}</p>
                    </div>
                  ) : (
                    <div className="max-w-xl">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className="px-3 py-1 bg-rose-600 text-white text-xs font-semibold rounded-full">{item.year}</span>
                        <div className="flex items-center text-yellow-400">
                          <i className="ri-star-fill mr-1"></i>
                          <span className="text-white font-medium">{item.rating}</span>
                        </div>
                      </div>
                      <h2 className="text-2xl md:text-4xl font-bold text-white mb-2">{item.title}</h2>
                      <p className="text-lg md:text-xl text-rose-300 mb-2">{item.subtitle}</p>
                      <p className="text-gray-300 text-sm md:text-base">{item.description}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center space-x-2 text-white/60 text-sm">
            <i className="ri-arrow-left-right-line animate-pulse"></i>
            <span>Scroll to explore</span>
          </div>

          {/* Creator Profile Overlay */}
          <div className="absolute top-0 left-0 right-0 p-4 md:p-8 z-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <img
                    src={creator.avatar}
                    alt={creator.name}
                    className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-3 border-white shadow-2xl"
                  />
                  {creator.verified && (
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-rose-500 to-amber-500 rounded-full flex items-center justify-center border-2 border-white">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                  )}
                </div>
                <div className="text-white">
                  <h1 className="text-xl md:text-2xl font-bold">{creator.name}</h1>
                  <p className="text-gray-300 text-sm">{creator.role} • {creator.country}</p>
                </div>
                <div className="flex-1"></div>
                <div className="hidden md:flex items-center space-x-3">
                  {displayShareCount > 0 && (
                    <div className="flex items-center space-x-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white/80 text-sm">
                      <i className="ri-share-forward-line text-xs"></i>
                      <span>{formatFollowers(displayShareCount)} shares</span>
                    </div>
                  )}
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer bg-white/20 text-white border border-white/40 hover:bg-white/30"
                  >
                    <i className="ri-share-line mr-2"></i>
                    Share Profile
                  </button>
                  <button
                    onClick={handleFollow}
                    className={`px-5 py-2 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                      displayIsFollowing
                        ? 'bg-white/20 text-white border border-white/40 hover:bg-white/30'
                        : 'bg-gradient-to-r from-rose-600 to-amber-600 text-white hover:from-rose-700 hover:to-amber-700'
                    }`}
                  >
                    <i className={`${displayIsFollowing ? 'ri-check-line' : 'ri-user-add-line'}`}></i>
                    {displayIsFollowing ? 'Following' : 'Follow'}
                    {dbCreatorId && (
                      <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs font-medium ml-0.5">
                        {formatFollowers(displayFollowerCount)}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Action Buttons */}
          <div className="md:hidden absolute bottom-16 left-0 right-0 px-4 z-10">
            <div className="flex items-center space-x-3">
              {displayShareCount > 0 && (
                <div className="flex items-center space-x-1 px-2.5 py-2 bg-white/10 backdrop-blur-sm rounded-lg text-white/80 text-xs">
                  <i className="ri-share-forward-line"></i>
                  <span>{formatFollowers(displayShareCount)}</span>
                </div>
              )}
              <button
                onClick={() => setShowShareModal(true)}
                className="py-2.5 px-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-sm bg-white/20 text-white border border-white/40"
              >
                <i className="ri-share-line"></i>
              </button>
              <button
                onClick={handleFollow}
                className={`flex-1 py-2.5 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-sm flex items-center justify-center gap-2 ${
                  displayIsFollowing
                    ? 'bg-white/20 text-white border border-white/40'
                    : 'bg-gradient-to-r from-rose-600 to-amber-600 text-white'
                }`}
              >
                <i className={`${displayIsFollowing ? 'ri-check-line' : 'ri-user-add-line'}`}></i>
                {displayIsFollowing ? 'Following' : 'Follow'}
                {dbCreatorId && (
                  <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                    {formatFollowers(displayFollowerCount)}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Stats */}
            <div className={`grid grid-cols-2 ${isAICreator ? 'md:grid-cols-4' : 'md:grid-cols-4'} gap-4 mb-8`}>
              <div className={`bg-gradient-to-br ${isAICreator ? 'from-orange-500 to-orange-600' : 'from-rose-500 to-rose-600'} rounded-xl p-5 text-center shadow-lg text-white relative overflow-hidden`}>
                <div className="text-2xl font-bold">
                  {isAICreator
                    ? (dbCreatorId ? formatFollowers(displayFollowerCount) : (creator as any).followersDisplay)
                    : formatFollowers(displayFollowerCount)
                  }
                </div>
                <div className="text-sm opacity-90 mt-1">Followers</div>
                {dbCreatorId && (
                  <div className="absolute top-2 right-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white/60 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-white/80"></span>
                    </span>
                  </div>
                )}
              </div>
              <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-5 text-center shadow-lg text-white">
                <div className="text-2xl font-bold">{creator.projects}</div>
                <div className="text-sm opacity-90 mt-1">Projects</div>
              </div>
              {isAICreator ? (
                <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl p-5 text-center shadow-lg text-white">
                  <div className="text-2xl font-bold">{aiCreatorData!.tools.length}</div>
                  <div className="text-sm opacity-90 mt-1">AI Tools</div>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl p-5 text-center shadow-lg text-white">
                  <div className="text-2xl font-bold">{creator.awards}</div>
                  <div className="text-sm opacity-90 mt-1">Awards</div>
                </div>
              )}
              <div className={`bg-gradient-to-br ${isAICreator ? 'from-rose-500 to-pink-600' : 'from-pink-500 to-rose-600'} rounded-xl p-5 text-center shadow-lg text-white relative overflow-hidden`}>
                <div className="text-2xl font-bold">{formatFollowers(displayShareCount)}</div>
                <div className="text-sm opacity-90 mt-1">Shares</div>
                {displayShareCount > 0 && (
                  <div className="absolute top-2 right-2">
                    <i className="ri-share-forward-line text-white/50 text-xs"></i>
                  </div>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
              <div className="border-b border-gray-200 overflow-x-auto">
                <nav className="flex space-x-8 px-6">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap cursor-pointer ${
                        activeTab === tab.id
                          ? `border-current ${accentText}`
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <i className={`${tab.icon} mr-2`}></i>
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                        <i className={`ri-information-line mr-2 ${accentText}`}></i>
                        About
                      </h3>
                      <p className="text-gray-700 leading-relaxed">{creator.bio}</p>
                    </div>

                    {/* AI Creator specific: Tools & Tech */}
                    {isAICreator && (
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                          <i className={`ri-tools-line mr-2 ${accentText}`}></i>
                          AI Tools &amp; Technology
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {aiCreatorData!.tools.map((tool, index) => (
                            <span key={index} className="px-4 py-2 bg-gradient-to-r from-orange-100 to-rose-100 text-orange-700 text-sm font-medium rounded-full">
                              {tool}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* AI Creator specific: Featured Work */}
                    {isAICreator && (
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                          <i className={`ri-movie-2-line mr-2 ${accentText}`}></i>
                          Featured Work
                        </h3>
                        <div className="p-4 bg-gradient-to-r from-orange-50 to-rose-50 rounded-xl border border-orange-100">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-orange-500 to-rose-500 rounded-xl text-white">
                              <i className="ri-film-line text-xl"></i>
                            </div>
                            <div>
                              <p className="font-bold text-gray-900">{aiCreatorData!.featuredWork}</p>
                              <p className="text-sm text-gray-500">{aiCreatorData!.category} • {aiCreatorData!.projects} project{aiCreatorData!.projects !== 1 ? 's' : ''}</p>
                            </div>
                            <div className="ml-auto">
                              <Link
                                to={createAIProjectUrl(aiCreatorData!.featuredProjectId, aiCreatorData!.featuredWork)}
                                className="px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                View in Gallery
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* AI Creator specific: Category */}
                    {isAICreator && (
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                          <i className={`ri-price-tag-3-line mr-2 ${accentText}`}></i>
                          Genre &amp; Category
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          <span className="px-4 py-2 bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 text-sm font-medium rounded-full">
                            {aiCreatorData!.category}
                          </span>
                          <span className="px-4 py-2 bg-gradient-to-r from-rose-100 to-pink-100 text-rose-700 text-sm font-medium rounded-full">
                            AI-Generated Content
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Regular creator: Specialties */}
                    {!isAICreator && (
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                          <i className="ri-star-line mr-2 text-rose-600"></i>
                          Specialties
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {creator.specialties.map((specialty, index) => (
                            <span key={index} className="px-4 py-2 bg-gradient-to-r from-rose-100 to-amber-100 text-rose-700 text-sm font-medium rounded-full">
                              {specialty}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Regular creator: Languages */}
                    {!isAICreator && creator.languages.length > 0 && (
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                          <i className="ri-global-line mr-2 text-rose-600"></i>
                          Languages
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {creator.languages.map((language, index) => (
                            <span key={index} className="px-4 py-2 bg-gradient-to-r from-teal-100 to-emerald-100 text-teal-700 text-sm font-medium rounded-full">
                              {language}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Mentioned In Articles */}
                    <MentionedInArticles
                      creatorId={creator.id}
                      creatorName={creator.name}
                      creatorSlug={creator.slug}
                      variant="inline"
                    />
                  </div>
                )}

                {/* AI Creator Projects Tab */}
                {activeTab === 'projects' && isAICreator && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-gray-900">AI Projects</h3>
                      <span className="text-sm text-gray-600">{aiCreatorData!.projects} project{aiCreatorData!.projects !== 1 ? 's' : ''}</span>
                    </div>
                    {/* Featured project card */}
                    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden shadow-xl">
                      <div className="relative h-48 md:h-64">
                        <img
                          src={aiCreatorData!.coverImage}
                          alt={aiCreatorData!.featuredWork}
                          className="w-full h-full object-cover opacity-60"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <span className="px-2.5 py-1 bg-orange-500 text-white text-xs font-semibold rounded-full mb-3 inline-block">
                            Featured Project
                          </span>
                          <h4 className="text-2xl font-bold text-white mb-1">{aiCreatorData!.featuredWork}</h4>
                          <p className="text-gray-300 text-sm">{aiCreatorData!.category} • Created with AI</p>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="flex flex-wrap gap-2 mb-4">
                          {aiCreatorData!.tools.map((tool, i) => (
                            <span key={i} className="px-3 py-1.5 bg-white/10 text-gray-300 text-xs font-medium rounded-lg">
                              {tool}
                            </span>
                          ))}
                        </div>
                        <Link
                          to={createAIProjectUrl(aiCreatorData!.featuredProjectId, aiCreatorData!.featuredWork)}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-rose-500 text-white text-sm font-semibold rounded-lg hover:from-orange-600 hover:to-rose-600 transition-all cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-play-circle-line"></i>
                          View in AI Dramas Gallery
                        </Link>
                      </div>
                    </div>

                    {/* Additional projects placeholder */}
                    {aiCreatorData!.projects > 1 && (
                      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Array.from({ length: Math.min(aiCreatorData!.projects - 1, 3) }).map((_, i) => (
                          <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all group">
                            <div className="flex items-center gap-4">
                              <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <i className="ri-film-line text-2xl text-orange-500"></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-gray-900 text-lg mb-1 group-hover:text-orange-600 transition-colors">Project {i + 2}</h4>
                                <p className="text-sm text-gray-500">{aiCreatorData!.category} • AI Generated</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">Published</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Filmography Tab (regular creators only) */}
                {activeTab === 'filmography' && !isAICreator && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-gray-900">Complete Filmography</h3>
                      <span className="text-sm text-gray-600">{filmography.length} projects</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {filmography.map((project) => (
                        <div key={project.id} className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300">
                          <div className="flex space-x-4 p-4">
                            <img src={project.poster} alt={project.title} className="w-24 h-36 object-cover rounded-lg shadow-md" />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-bold text-gray-900 text-lg mb-1 group-hover:text-rose-600 transition-colors">{project.title}</h4>
                              <p className="text-sm text-gray-600 mb-2">{project.role}</p>
                              <p className="text-xs text-gray-500 mb-2">{project.genre}</p>
                              <div className="flex items-center mb-2">
                                <div className="flex items-center text-yellow-500 mr-3">
                                  <i className="ri-star-fill text-sm"></i>
                                  <span className="text-sm text-gray-700 ml-1 font-medium">{project.rating}</span>
                                </div>
                                <span className="text-xs text-gray-500">{project.episodes} episodes</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                  project.status === 'Released' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {project.status}
                                </span>
                                <span className="text-xs text-gray-500">{project.year}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Awards Tab (regular creators only) */}
                {activeTab === 'awards' && !isAICreator && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-gray-900">Awards &amp; Recognition</h3>
                      <span className="text-sm text-gray-600">{awards.length} awards</span>
                    </div>
                    <div className="space-y-4">
                      {awards.map((award, index) => (
                        <div key={index} className="flex items-start space-x-4 p-5 border border-gray-200 rounded-xl hover:shadow-lg transition-all bg-gradient-to-r from-white to-gray-50">
                          <div className={`w-14 h-14 ${award.color} rounded-full flex items-center justify-center flex-shrink-0 shadow-md`}>
                            <i className={`${award.icon} text-2xl`}></i>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-900 text-lg">{award.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{award.event} • {award.year}</p>
                            <p className="text-sm text-rose-600 mt-1 font-medium">For: {award.project}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Fan Reviews Tab */}
                {activeTab === 'reviews' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-gray-900">Fan Reviews</h3>
                    </div>
                    <div className="space-y-4">
                      {fanReviews.map((review) => (
                        <div key={review.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all">
                          <div className="flex items-start space-x-4">
                            <img src={review.avatar} alt={review.user} className="w-12 h-12 rounded-full object-cover" />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-semibold text-gray-900">{review.user}</h4>
                                <span className="text-xs text-gray-500">{review.date}</span>
                              </div>
                              <div className="flex text-yellow-400 mb-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <i key={star} className={`${star <= review.rating ? 'ri-star-fill' : 'ri-star-line'} text-sm`}></i>
                                ))}
                              </div>
                              <p className="text-gray-700 leading-relaxed mb-3">{review.comment}</p>
                              <button className="text-sm text-gray-500 hover:text-rose-600 transition-colors cursor-pointer">
                                <i className="ri-thumb-up-line mr-1"></i>
                                Helpful ({review.likes})
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Connect Card */}
            {!isAICreator ? (
              <div className="bg-gradient-to-br from-rose-600 to-amber-600 rounded-xl shadow-lg p-6 text-white">
                <h3 className="text-lg font-bold mb-4 flex items-center">
                  <i className="ri-heart-line mr-2"></i>
                  Connect with {creator.name.split(' ')[0]}
                </h3>
                <div className="space-y-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="opacity-90">Response Rate</span>
                      <span className="font-bold">{creator.responseRate}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="opacity-90">Avg Response Time</span>
                      <span className="font-bold">{creator.avgResponseTime}</span>
                    </div>
                  </div>
                  <button 
                    disabled
                    className="w-full py-3 bg-white/60 text-rose-400 rounded-lg font-semibold whitespace-nowrap cursor-not-allowed opacity-60"
                  >
                    <i className="ri-message-3-line mr-2"></i>
                    Send Fan Message
                  </button>
                </div>
              </div>
            ) : (
              /* AI Creator Info Card */
              <div className="bg-gradient-to-br from-orange-500 to-rose-500 rounded-xl shadow-lg p-6 text-white">
                <h3 className="text-lg font-bold mb-4 flex items-center">
                  <i className="ri-robot-2-line mr-2"></i>
                  AI Creator Profile
                </h3>
                <div className="space-y-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="opacity-90">Category</span>
                      <span className="font-bold">{aiCreatorData!.category}</span>
                    </div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="opacity-90">Projects</span>
                      <span className="font-bold">{aiCreatorData!.projects}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="opacity-90">Country</span>
                      <span className="font-bold">{aiCreatorData!.country}</span>
                    </div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-xs opacity-80 mb-2">Tools Used</p>
                    <div className="flex flex-wrap gap-1.5">
                      {aiCreatorData!.tools.map((tool, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white/20 text-white text-xs rounded-md font-medium">
                          {tool}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Link
                    to={createAIProjectUrl(aiCreatorData!.featuredProjectId, aiCreatorData!.featuredWork)}
                    className="block w-full py-3 bg-white text-orange-600 rounded-lg font-semibold whitespace-nowrap cursor-pointer text-center hover:bg-orange-50 transition-colors"
                  >
                    <i className="ri-film-line mr-2"></i>
                    View Featured Project
                  </Link>
                </div>
              </div>
            )}

            {/* Social Media (regular creators only) */}
            {!isAICreator && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-links-line mr-2 text-rose-600"></i>
                  Social Media
                </h3>
                <div className="space-y-3">
                  {creator.socialMedia.instagram && (
                    <a href={`https://instagram.com/${creator.socialMedia.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-700 hover:text-pink-600 transition-colors p-2 rounded-lg hover:bg-pink-50">
                      <i className="ri-instagram-line mr-3 text-xl"></i>
                      <span className="font-medium">{creator.socialMedia.instagram}</span>
                    </a>
                  )}
                  {creator.socialMedia.twitter && (
                    <a href={`https://twitter.com/${creator.socialMedia.twitter.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-700 hover:text-blue-500 transition-colors p-2 rounded-lg hover:bg-blue-50">
                      <i className="ri-twitter-line mr-3 text-xl"></i>
                      <span className="font-medium">{creator.socialMedia.twitter}</span>
                    </a>
                  )}
                  {creator.socialMedia.website && (
                    <a href={`https://${creator.socialMedia.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-700 hover:text-rose-600 transition-colors p-2 rounded-lg hover:bg-rose-50">
                      <i className="ri-global-line mr-3 text-xl"></i>
                      <span className="font-medium">{creator.socialMedia.website}</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Similar Creators */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <i className={`ri-user-star-line mr-2 ${accentText}`}></i>
                {isAICreator ? 'More AI Creators' : 'Similar Creators'}
              </h3>
              <div className="space-y-4">
                {similarCreators.map((similar: any) => (
                  <Link
                    key={similar.id}
                    to={createCreatorUrl(similar.slug)}
                    className={`flex items-center space-x-3 p-3 rounded-lg hover:bg-orange-50 transition-colors border border-transparent ${accentBorder}`}
                  >
                    <img src={similar.avatar} alt={similar.name} className="w-12 h-12 rounded-full object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{similar.name}</p>
                      <p className="text-sm text-gray-600">{similar.role}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        {similar.followersDisplay || formatFollowers(similar.followers)}
                      </p>
                      <p className="text-xs text-gray-400">followers</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={`${creator.name} — ${creator.role}`}
        url={profileUrl}
        onShare={handleShare}
      />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="login"
      />
    </div>
  );
};

export default CreatorDetailPage;
